/**
 * BlockFlowCoordinator - Generic coordinator for StrategyBlock entities on flow canvas
 *
 * Sprint: sprint-4, Task: T005.3
 * Requirement: FR-P3-003 - implements FlowCoordinator<StrategyBlock, StrategyBlockService>
 *
 * Architecture:
 * - Manages StrategyBlock lifecycle (create, update, delete)
 * - Computes transitions on-demand from block.action.edges[] (NO cache - FR-P3-005)
 * - Coordinates with BlockRegistry for data storage
 * - Controls Canvas through imperative API
 * - Firebase integration for persistence
 *
 * Pattern: Instance-based implementing generic FlowCoordinator interface
 * Mirrors: ScreenFlowCoordinator structure
 *
 * KEY DIFFERENCES from ScreenFlowCoordinator:
 * - StrategyBlock instead of Screen
 * - StrategyBlockService for block persistence
 * - Transitions from block.action.edges[] (simple) vs. screen.struct (complex)
 * - BlockTransitionManager instead of ScreenTransitionManager
 * - BlockRegistry instead of ScreenRegistry
 *
 * Usage:
 * ```typescript
 * // Create coordinator instance
 * const coordinator = new BlockFlowCoordinator('proj-1', 'strat-1');
 *
 * // Load blocks from Firebase
 * await coordinator.loadBlocksFromFirebase();
 *
 * // Set canvas handle
 * coordinator.setCanvasHandle(canvasHandle);
 *
 * // Use coordinator (generic interface methods)
 * const result = coordinator.createEntity({}, { x: 100, y: 200 });
 *
 * // Cleanup on unmount
 * coordinator.destroy();
 * ```
 */

import type { StrategyBlock } from '@segmently/schema-models/strategy';
import type { Position } from '@segmently/schema-models/strategy';
type XyPoint = Position;
import type { FlowCoordinator, CreateEntityResult, CreateTransitionInput, LoadingCallbacks } from '../shared/FlowCoordinator';
import type { ReactFlowCanvasHandle } from '@modules/reactFlow/types/flow-canvas-types';
import { blockRegistry } from '@modules/reactFlow/services/block/BlockRegistry';
import { blockConverter } from '@modules/reactFlow/utils/converters/BlockConverterService';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { blockTransitionManager } from '@modules/reactFlow/utils/transformers/BlockTransitionManager';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import { MOCK_TEMPLATE_BLOCKS } from '@modules/reactFlow/services/block/mockTemplateBlocks';
import { BLOCK_DIMENSIONS } from '@modules/reactFlow/constants/blockDimensionConfig';
import type { LibraryAdaptationMode } from '@modules/shared-types';
import { readLibraryAdaptationState } from '@modules/strategy-blocks/utils/libraryAdaptationState';

/** UXR C3: minimal subtype shape the coordinator needs to materialize a custom block. */
export interface BlockSubtypeTemplateSource {
  slug: string;
  name: string;
  description: string;
  enabled: boolean;
  defaultInstructions: Array<{ id?: string; level: string; text: string }>;
}

export const SUBTYPE_TEMPLATE_ID_PREFIX = 'subtype::';

/**
 * Block Examples Library palette cards.
 * Template id format: `example::<source>::<exampleId>` (source: project|system).
 */
export const EXAMPLE_TEMPLATE_ID_PREFIX = 'example::';
/** Opens the explicit immediate/as-is decision surface; never used by default Add. */
export const EXAMPLE_SECONDARY_TEMPLATE_ID_PREFIX = 'example-secondary::';

export interface ExampleInsertedInfo {
  blockId: string;
  entityId: string;
  /** Created block name — the toast should prefer the EXAMPLE name via exampleId. */
  name: string;
  exampleId: string;
  source: 'project' | 'system';
  mode: LibraryAdaptationMode;
  adapt: boolean;
  adaptTaskId?: string;
}

export interface ExampleInsertGateRequest {
  exampleId: string;
  source: 'project' | 'system';
  position: { x: number; y: number };
}

export interface ExampleInsertGateDecision {
  adapt: boolean;
  variableMapping?: Record<string, unknown>;
  /**
   * Operator-chosen adaptation model (Insert dialog selector). Only honored
   * on adapt=true — never silently defaulted client-side (operator decision
   * 2026-08-05: the model is chosen at the launch surface).
   */
  model?: string;
}

/** Resolves null on operator cancel — nothing is written. */
export type ExampleInsertGate = (
  request: ExampleInsertGateRequest,
) => Promise<ExampleInsertGateDecision | null>;

/** Pending payload for the example-insert roundtrip; null = cleared. */
export interface ExampleInsertPendingInfo {
  exampleId: string;
  source: 'project' | 'system';
}

export interface CreateBlockResult {
  firebaseBlock: StrategyBlock;
}

// ============================================================================
// CLASS DEFINITION
// ============================================================================

/**
 * BlockFlowCoordinator - Concrete implementation of FlowCoordinator for StrategyBlock entities
 *
 * Implements: FlowCoordinator<StrategyBlock, StrategyBlockService>
 * Mirrors: ScreenFlowCoordinator structure (FR-P3-003)
 */
export class BlockFlowCoordinator implements FlowCoordinator<StrategyBlock, StrategyBlockService> {
  private initialized: boolean = false;
  private blockService: StrategyBlockService | null = null;
  private unsubscribeFirebase?: () => void;
  private projectId: string;
  private strategyId: string;
  private canvasHandle: ReactFlowCanvasHandle | null = null;
  private loadingCallbacks?: LoadingCallbacks;
  private launchBlockId: string | null = null;  // Cache for launch block ID
  /** UXR C3: live project subtype snapshot for `subtype::<slug>` template drops. */
  private blockSubtypes: BlockSubtypeTemplateSource[] = [];
  /**
   * Block Examples Library insert gate (set by the editor): computes the
   * contract fit and — on candidates/conflicts — opens the Insert dialog.
   * Resolves null when the operator cancels. Absent gate = adapt-by-default
   * with server auto-resolution.
   */
  private exampleInsertGate: ExampleInsertGate | null = null;
  private exampleInsertedListener: ((info: ExampleInsertedInfo) => void) | null = null;
  /** Pending-roundtrip listener (editor snackbar); null payload = cleared. */
  private exampleInsertPendingListener: ((info: ExampleInsertPendingInfo | null) => void) | null = null;
  /** Double-insert guard: one from-library roundtrip at a time. */
  private exampleInsertPending = false;

  /**
   * Create BlockFlowCoordinator instance
   *
   * Sprint: sprint-4, Task: T005.3
   * Requirement: FR-P3-003 - Constructor accepts projectId, strategyId and initializes Firebase
   *
   * @param projectId - Parent project ID
   * @param strategyId - Parent strategy ID
   */
  constructor(projectId: string, strategyId: string) {
    this.projectId = projectId;
    this.strategyId = strategyId;
    this.blockService = new StrategyBlockService(projectId, strategyId);
    this.initialized = true;
    console.log('[BlockCoordinator] Initialized with:', { projectId, strategyId });
  }

  // ============================================================================
  // SECTION 2: GETTER METHODS (FlowCoordinator interface)
  // ============================================================================

  /**
   * Get project ID
   * @returns Project ID passed to constructor
   */
  public getProjectId(): string {
    return this.projectId;
  }

  /**
   * Get strategy ID
   * @returns Strategy ID passed to constructor
   */
  public getStrategyId(): string {
    return this.strategyId;
  }

  /**
   * Get StrategyBlockService instance
   * @returns StrategyBlockService or null if destroyed
   */
  public getBlockService(): StrategyBlockService | null {
    return this.blockService;
  }

  /**
   * Get Canvas handle (FlowCoordinator interface)
   * @returns ReactFlowCanvasHandle or null
   */
  public getCanvasHandle(): ReactFlowCanvasHandle | null {
    return this.canvasHandle;
  }

  /**
   * Get all blocks (FlowCoordinator interface)
   * @returns Array of all StrategyBlock entities
   */
  public getAllEntities(): StrategyBlock[] {
    return this.getAllBlocks();
  }

  /**
   * Get all transitions (FlowCoordinator interface)
   * FR-P3-005: Computed on-demand from block.action.edges[] (NO cache)
   * @returns Array of all EntityTransition objects
   */
  public getAllTransitions(): EntityTransition[] {
    return this.computeAllTransitions();
  }

  // ============================================================================
  // SECTION 3: INITIALIZATION & LIFECYCLE (FlowCoordinator interface)
  // ============================================================================

  /**
   * Initialize coordinator (FlowCoordinator interface)
   *
   * @param projectId - Project ID (unused, already set in constructor)
   * @param strategyId - Strategy ID (unused, already set in constructor)
   * @param launchBlockId - Optional launch block ID (optimization to avoid Firebase query)
   */
  public async initialize(projectId?: string, strategyId?: string, launchBlockId?: string): Promise<void> {
    if (!this.initialized) {
      this.blockService = new StrategyBlockService(this.projectId, this.strategyId);

      // Cache launch block ID if provided
      if (launchBlockId) {
        this.launchBlockId = launchBlockId;
        console.log('[BlockCoordinator] Initialized with cached launchBlockId:', launchBlockId);
      }

      this.initialized = true;
      console.log('[BlockCoordinator] Initialized');
    }
  }

  /**
   * Check if coordinator is initialized (FlowCoordinator interface)
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Destroy coordinator and cleanup (FlowCoordinator interface)
   */
  public destroy(): void {
    if (this.unsubscribeFirebase) {
      this.unsubscribeFirebase();
      this.unsubscribeFirebase = undefined;
    }

    // Clear blockRegistry to prevent block leakage between strategies
    blockRegistry.clear();

    this.blockService = null;
    this.canvasHandle = null;
    this.initialized = false;
    console.log('[BlockCoordinator] Destroyed');
  }

  /**
   * Load existing blocks from Firebase to BlockRegistry
   * Called automatically when canvas handle is set
   */
  async loadBlocksFromFirebase(): Promise<void> {
    if (!this.blockService) {
      console.warn('[BlockCoordinator] Cannot load blocks - service not initialized');
      return;
    }

    console.log('[BlockCoordinator] Loading blocks from Firebase...');
    const loadedBlocks = await this.blockService.list();
    // Immediate adaptation is pre-placement: its internal screenless shell is
    // never shown. A successful atomic save changes status to adapted and the
    // normal block appears; terminal failures delete the shell server-side.
    const blocks = loadedBlocks.filter((block) => {
      const adaptation = readLibraryAdaptationState(block);
      return adaptation?.mode !== 'immediate' || adaptation.status === 'adapted';
    });

    // CRITICAL: Ensure all blocks have entityId before registering
    blocks.forEach(block => {
      if (!block.entityId) {
        console.error(`[BlockCoordinator] ❌ CRITICAL: Block ${block.id} missing entityId - generating new UUID`);
        (block as any).entityId = crypto.randomUUID();
      }
      blockRegistry.register(block);
    });

    console.log(`[BlockCoordinator] Loaded ${blocks.length} blocks, ${this.computeAllTransitions().length} transitions`);
  }

  /**
   * Refresh one block after a backend task completes.
   *
   * Block generation/update tasks mutate Firestore asynchronously. The blocks
   * canvas intentionally does not keep a live listener on the full blocks
   * collection, so task completion performs a single targeted read and updates
   * the registry/canvas node/edges for the affected block only.
   *
   * G3d: a block that arrives while a generation is running is refreshed with
   * `selectNewNode: false` — a run that lands 20 blocks must not take the
   * owner's selection away 20 times. A task-completion refresh keeps selecting.
   */
  async refreshBlockFromFirebase(
    blockRef: string,
    options: { selectNewNode?: boolean } = {},
  ): Promise<StrategyBlock | null> {
    if (!this.blockService) {
      console.warn('[BlockCoordinator] Cannot refresh block - service not initialized');
      return null;
    }

    const existing = blockRegistry.get(blockRef)
      || blockRegistry.getAll().find(block => block.id === blockRef || block.entityId === blockRef);
    const firestoreId = existing?.id || blockRef;
    const latest = await this.blockService.get(firestoreId);

    if (!latest) {
      console.warn('[BlockCoordinator] Cannot refresh block - Firestore doc not found:', firestoreId);
      return null;
    }

    this.applyRefreshedBlock(latest, existing, blockRef, options);

    if (this.canvasHandle) {
      this.syncAllEdges();
    }

    return latest;
  }

  /**
   * Refresh MANY blocks with ONE collection read (G3d).
   *
   * A generation lands its planned blocks in one burst; N targeted `get()`s for the
   * same burst are N round-trips for data a single `list()` already carries. Node and
   * registry updates are exactly the ones a per-block refresh performs, and the edges
   * are synced once at the end.
   */
  async refreshBlocksFromFirebase(
    blockRefs: string[],
    options: { selectNewNode?: boolean } = {},
  ): Promise<StrategyBlock[]> {
    if (!this.blockService) {
      console.warn('[BlockCoordinator] Cannot refresh blocks - service not initialized');
      return [];
    }
    if (blockRefs.length === 0) return [];

    const all = await this.blockService.list();
    const byRef = new Map<string, StrategyBlock>();
    for (const block of all) {
      if (block.id) byRef.set(block.id, block);
      if (block.entityId) byRef.set(block.entityId, block);
    }

    const refreshed: StrategyBlock[] = [];
    for (const blockRef of blockRefs) {
      const existing = blockRegistry.get(blockRef)
        || blockRegistry.getAll().find(block => block.id === blockRef || block.entityId === blockRef);
      const latest = byRef.get(existing?.id || blockRef) || byRef.get(blockRef);

      if (!latest) {
        console.warn('[BlockCoordinator] Cannot refresh block - Firestore doc not found:', blockRef);
        continue;
      }

      this.applyRefreshedBlock(latest, existing, blockRef, options);
      refreshed.push(latest);
    }

    if (refreshed.length > 0 && this.canvasHandle) {
      this.syncAllEdges();
    }

    return refreshed;
  }

  /** Registry + canvas node for one freshly read block (no edge sync — the caller does it once). */
  private applyRefreshedBlock(
    latest: StrategyBlock,
    existing: StrategyBlock | undefined,
    blockRef: string,
    options: { selectNewNode?: boolean },
  ): void {
    if (!latest.entityId) {
      (latest as any).entityId = existing?.entityId || latest.id || blockRef;
    }

    const entityId = latest.entityId!;
    blockRegistry.register(latest);

    if (!this.canvasHandle) return;

    const canvasBlock = blockConverter.toCanvasBlock(latest);
    if (existing) {
      this.canvasHandle.updateNodeData(entityId, {
        ...(latest as any),
        handles: canvasBlock.connectionHandles || [],
        isLaunchScreen: latest.isLaunchBlock === true,
      });
      return;
    }

    this.canvasHandle.addNode({
      entityId,
      entityType: 'block',
      nodeType: 'strategy-block',
      position: canvasBlock.position,
      handles: canvasBlock.connectionHandles || [],
      isLaunchScreen: latest.isLaunchBlock === true,
    });
    if (options.selectNewNode !== false) {
      this.canvasHandle.selectNodes([entityId]);
    }
  }

  setCanvasHandle(handle: ReactFlowCanvasHandle | null): void {
    this.canvasHandle = handle;

    if (handle && this.initialized) {
      this.loadExistingBlocksToCanvas();
    } else if (handle && !this.initialized) {
      console.warn('[BlockCoordinator] Canvas ready but not initialized - call initialize() first');
    }
  }

  /**
   * Set loading callbacks for Firebase synchronization state
   *
   * Sprint: firebase-sync-loading
   * Implements: FlowCoordinator.setLoadingCallbacks()
   */
  setLoadingCallbacks(callbacks: LoadingCallbacks): void {
    this.loadingCallbacks = callbacks;
  }

  /**
   * Wrap async Firebase operation with loading state callbacks
   *
   * Handles: onSyncStart → operation → onSyncEnd (success) or onSyncError (failure)
   * Ensures: onSyncEnd is always called (even on error, after onSyncError)
   *
   * Sprint: firebase-sync-loading
   * Pattern: Template method pattern for async operation lifecycle
   * Note: UI component manages operation counter for race condition protection
   *
   * @param operation - Human-readable operation description (e.g., "Creating block")
   * @param fn - Async function to execute
   * @returns Promise with operation result
   */
  private async wrapWithLoading<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    try {
      this.loadingCallbacks?.onSyncStart?.(operation);
      const result = await fn();
      this.loadingCallbacks?.onSyncEnd?.();
      return result;
    } catch (error) {
      this.loadingCallbacks?.onSyncError?.(error as Error, operation);
      this.loadingCallbacks?.onSyncEnd?.(); // Always end loading state
      throw error;
    }
  }

  /**
   * Get current launch block ID
   *
   * Returns the block marked with isLaunchBlock === true.
   * Uses cached value if available, otherwise queries all blocks.
   *
   * Implements: FlowCoordinator.getLaunchEntityId()
   *
   * @returns Promise resolving with launch block ID or null
   */
  async getLaunchEntityId(): Promise<string | null> {
    // Return cached value if available
    if (this.launchBlockId) {
      return this.launchBlockId;
    }

    // Query all blocks to find launch block
    const allBlocks = this.getAllBlocks();
    const launchBlock = allBlocks.find(block => block.isLaunchBlock === true);

    // Cache result
    this.launchBlockId = launchBlock?.entityId ?? null;

    return this.launchBlockId;
  }

  /**
   * Set launch block (automatically unsets old launch)
   *
   * Handles:
   * - Deleting ALL incoming edges to the new launch block
   * - Backing up affected source blocks for undo support
   * - Unsetting previous launch block (isLaunchBlock = false)
   * - Setting new launch block (isLaunchBlock = true)
   * - Updating Canvas visual indicators (NodeData.isLaunchScreen flag)
   * - Firebase persistence
   *
   * Implements: FlowCoordinator.setLaunchEntity()
   * Pattern: Similar to deleteBlocks - returns deletion results for undo
   *
   * @param entityId - Block entity ID to set as launch
   * @returns Promise resolving with deleted transition IDs and affected blocks backup
   */
  async setLaunchEntity(entityId: string): Promise<{
    deletedTransitionIds: string[];
    affectedEntitiesBackup: Map<string, StrategyBlock>;
  }> {
    return this.wrapWithLoading('Setting launch block', async () => {
      console.log('[BlockCoordinator] Setting launch block:', entityId);

      // Step 1: Get previous launch block ID (if any)
      const previousLaunchId = await this.getLaunchEntityId();

      // Step 1.5: Find and delete incoming edges to launch block
      const allTransitions = this.computeAllTransitions();
      const incomingTransitions = allTransitions.filter(t =>
        t.targetEntityId === entityId
      );
      const deletedTransitionIds = incomingTransitions.map(t => t.id);

      // Step 1.6: Backup affected source blocks BEFORE modification
      const affectedBlocksBackup = new Map<string, StrategyBlock>();
      for (const transition of incomingTransitions) {
        const sourceBlock = blockRegistry.get(transition.sourceEntityId);
        if (sourceBlock) {
          // Deep copy ENTIRE block before any changes (includes all edges in action.edges)
          affectedBlocksBackup.set(
            transition.sourceEntityId,
            JSON.parse(JSON.stringify(sourceBlock))
          );
        }
      }

      // Step 1.7: Delete transitions (if any exist)
      if (deletedTransitionIds.length > 0) {
        await this.deleteTransitions(deletedTransitionIds);
        console.log(`[BlockCoordinator] Deleted ${deletedTransitionIds.length} incoming edge(s) to launch block`);
      }

      // Step 2: Unset previous launch block (if different from new one)
      if (previousLaunchId && previousLaunchId !== entityId) {
        const previousBlock = blockRegistry.get(previousLaunchId);
        if (previousBlock && this.blockService) {
          const updatedPrevious = { ...previousBlock, isLaunchBlock: false };
          await this.blockService.update(previousBlock.id, updatedPrevious);
          blockRegistry.update(previousLaunchId, updatedPrevious);

          // Update Canvas node visual indicator
          if (this.canvasHandle) {
            this.canvasHandle.updateNodeData(previousLaunchId, {
              isLaunchScreen: false
            });
          }

          console.log('[BlockCoordinator] Unset previous launch block:', previousLaunchId);
        }
      }

      // Step 3: Set new launch block
      const newBlock = blockRegistry.get(entityId);
      if (!newBlock) {
        throw new Error(`Block not found: ${entityId}`);
      }

      if (!this.blockService) {
        throw new Error('BlockService not initialized');
      }

      const updatedBlock = { ...newBlock, isLaunchBlock: true };
      await this.blockService.update(newBlock.id, updatedBlock);
      blockRegistry.update(entityId, updatedBlock);

      // Update cache
      this.launchBlockId = entityId;

      // Update Canvas node visual indicator
      if (this.canvasHandle) {
        this.canvasHandle.updateNodeData(entityId, {
          isLaunchScreen: true
        });
      }

      console.log('[BlockCoordinator] ✅ Launch block set:', entityId);

      // Step 4: Return deletion results for undo support
      return { deletedTransitionIds, affectedEntitiesBackup: affectedBlocksBackup };
    });
  }

  // ============================================================================
  // SECTION 4: ENTITY OPERATIONS (FlowCoordinator interface)
  // ============================================================================

  /**
   * Create entity (FlowCoordinator interface)
   * Sprint-12: Support template-based block creation (mirrors ScreenFlowCoordinator)
   */
  /** UXR C3: refresh the live subtype snapshot used by `subtype::<slug>` drops. */
  setBlockSubtypes(subtypes: BlockSubtypeTemplateSource[]): void {
    this.blockSubtypes = subtypes;
  }

  /** Block Examples Library: install the fit-gate (see field doc). */
  setExampleInsertedListener(listener: ((info: ExampleInsertedInfo) => void) | null): void {
    this.exampleInsertedListener = listener;
  }

  /**
   * Center the node bounds on the requested point and nudge away from
   * occupied positions (down-right steps) so the inserted block is visible
   * and not stacked on an existing node.
   */
  private resolveExamplePlacement(center: { x: number; y: number }): { x: number; y: number } {
    const candidate = {
      x: Math.round(center.x - BLOCK_DIMENSIONS.WIDTH / 2),
      y: Math.round(center.y - BLOCK_DIMENSIONS.HEIGHT / 2),
    };
    const occupied = blockRegistry.getAll()
      .map((block) => block.position)
      .filter((pos): pos is { x: number; y: number } =>
        Boolean(pos) && typeof pos!.x === 'number' && typeof pos!.y === 'number');
    // Two nodes intersect while both deltas are under the FULL node size —
    // a 0.75 factor still allowed a 105×186px overlap (re-review of design
    // Must Fix #4). Margin keeps a visible gutter between neighbors.
    const MARGIN = 24;
    const overlaps = (pos: { x: number; y: number }): boolean => occupied.some((other) =>
      Math.abs(other.x - pos.x) < BLOCK_DIMENSIONS.WIDTH + MARGIN
      && Math.abs(other.y - pos.y) < BLOCK_DIMENSIONS.HEIGHT + MARGIN);
    for (let step = 0; step < 12 && overlaps(candidate); step += 1) {
      candidate.x += BLOCK_DIMENSIONS.WIDTH / 4 + MARGIN;
      candidate.y += BLOCK_DIMENSIONS.HEIGHT / 4 + MARGIN;
    }
    return candidate;
  }

  setExampleInsertGate(gate: ExampleInsertGate | null): void {
    this.exampleInsertGate = gate;
  }

  /**
   * Perceivable-progress listener for the from-library roundtrip (server
   * writes block + screens + spawns the adapt task — seconds of dead air
   * otherwise). Fires AFTER the gate confirm, clears (null) in finally.
   */
  setExampleInsertPendingListener(listener: ((info: ExampleInsertPendingInfo | null) => void) | null): void {
    this.exampleInsertPendingListener = listener;
  }

  async createEntity(data: Partial<StrategyBlock>, position: { x: number; y: number }): Promise<CreateEntityResult<StrategyBlock>> {
    // Try to get template if templateId is provided
    const templateId = (data as any).templateId;

    // Block Examples Library: `example::<source>::<id>` creates a persisted,
    // screenless staged shell. Immediate/as-is are explicit secondary actions
    // and do not overload the default canvas insertion gesture.
    const isDefaultLibraryInsert = typeof templateId === 'string'
      && templateId.startsWith(EXAMPLE_TEMPLATE_ID_PREFIX);
    const isSecondaryLibraryInsert = typeof templateId === 'string'
      && templateId.startsWith(EXAMPLE_SECONDARY_TEMPLATE_ID_PREFIX);
    if (isDefaultLibraryInsert || isSecondaryLibraryInsert) {
      const prefix = isSecondaryLibraryInsert
        ? EXAMPLE_SECONDARY_TEMPLATE_ID_PREFIX
        : EXAMPLE_TEMPLATE_ID_PREFIX;
      const [source, exampleId] = templateId.slice(prefix.length).split('::');
      if ((source !== 'project' && source !== 'system') || !exampleId) {
        throw new Error(`Malformed example template id: ${templateId}`);
      }
      // One from-library roundtrip at a time — a repeat click while the
      // server writes reads as a dead click and double-inserts otherwise.
      if (this.exampleInsertPending) {
        throw new Error('Example insert already in progress');
      }
      // Placement: the incoming position is the viewport CENTER (or drop
      // cursor) — center the node's BOUNDS on it and step away from occupied
      // spots, otherwise the 422×316 node hangs below the fold stacked on an
      // existing block and the silent insert reads as a dead click
      // (design-review Must Fix #4).
      const placedPosition = this.resolveExamplePlacement(position);

      // The default gesture is always staged and does not open a gate. Only
      // the explicit secondary action may choose immediate AI or as-is.
      const decision = isSecondaryLibraryInsert
        ? await this.exampleInsertGate?.({ exampleId, source, position: placedPosition })
        : undefined;
      if (isSecondaryLibraryInsert && !decision) {
        throw new Error('Example insert cancelled');
      }
      const mode: LibraryAdaptationMode = isSecondaryLibraryInsert
        ? decision!.adapt ? 'immediate' : 'as-is'
        : 'staged';

      // Pending covers the whole server roundtrip THROUGH the node landing —
      // gate/dialog time is deliberately excluded (dialog open ≠ inserting).
      this.exampleInsertPending = true;
      this.exampleInsertPendingListener?.({ exampleId, source });
      try {
        const { BlockLibraryInstantiationService } = await import('@modules/strategy-blocks/services/BlockLibraryInstantiationService');
        const service = new BlockLibraryInstantiationService(this.projectId, this.strategyId);
        const result = await service.instantiate(exampleId, {
          source,
          position: placedPosition,
          mode,
          ...(mode !== 'as-is' ? { evidence: { mode: 'strategy-default' as const } } : {}),
          ...(decision?.variableMapping ? { variableMapping: decision.variableMapping as never } : {}),
          ...(mode === 'immediate' && decision?.model ? { model: decision.model } : {}),
          idempotencyKey: globalThis.crypto?.randomUUID?.()
            ?? `library-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        });

        // The server wrote the screenless shell — mirror it onto the registry
        // and select it so the staged inspector can open immediately.
        const created = await this.blockService!.get(result.blockId);
        const createdBlock = (created ?? {
          id: result.blockId,
          entityId: result.entityId,
          name: result.name,
          position: placedPosition,
        }) as StrategyBlock;
        if (result.mode !== 'immediate' || result.adaptationState?.status === 'adapted') {
          blockRegistry.register(createdBlock);
          const canvasBlock = blockConverter.toCanvasBlock(createdBlock);
          this.canvasHandle?.addNode({
            entityId: createdBlock.entityId!,
            entityType: 'block',
            nodeType: 'strategy-block',
            position: canvasBlock.position,
            handles: canvasBlock.connectionHandles || [],
            isLaunchScreen: false,
          });
          // Staged/as-is are inserted immediately. Immediate remains hidden
          // until task completion reveals the fully adapted block.
          this.canvasHandle?.selectNodes([createdBlock.entityId!]);
        }
        this.exampleInsertedListener?.({
          blockId: result.blockId,
          entityId: createdBlock.entityId!,
          name: result.name,
          exampleId,
          source,
          mode: result.mode,
          adapt: result.mode === 'immediate',
          ...(result.adaptTaskId ? { adaptTaskId: result.adaptTaskId } : {}),
        });
        return { entity: createdBlock };
      } finally {
        this.exampleInsertPending = false;
        this.exampleInsertPendingListener?.(null);
      }
    }

    // UXR C3: a subtype card materializes as a custom block with the exact slug
    // and a copy of the catalog defaults (same semantics as BlockFormDialog's
    // handleSubtypeChange — the block owns its instructions after assignment).
    if (typeof templateId === 'string' && templateId.startsWith(SUBTYPE_TEMPLATE_ID_PREFIX)) {
      const slug = templateId.slice(SUBTYPE_TEMPLATE_ID_PREFIX.length);
      const subtype = this.blockSubtypes.find((item) => item.slug === slug && item.enabled);
      if (!subtype) {
        throw new Error(`Block subtype "${slug}" is not available in this project`);
      }
      const uuid = crypto.randomUUID();
      const newBlock: StrategyBlock = {
        id: uuid,
        entityId: uuid,
        blockType: 'custom',
        customSubtype: subtype.slug,
        name: subtype.name,
        description: subtype.description,
        instructions: subtype.defaultInstructions.map((instruction) => ({ ...instruction })),
        position,
      } as unknown as StrategyBlock;

      const result = await this.createBlock(newBlock, position);
      return {
        entity: result.firebaseBlock,
      };
    }

    if (templateId) {
      // Find template by ID (same pattern as ScreenFlowCoordinator)
      const template = MOCK_TEMPLATE_BLOCKS.find(b => b.id === templateId);

      if (template) {
        console.log('[BlockFlowCoordinator] Creating block from template:', {
          templateId,
          blockType: template.blockType,
          name: template.name
        });

        // Create new block from template (copy ALL template data)
        const uuid = crypto.randomUUID();
        const newBlock: StrategyBlock = {
          ...template,      // Copy ALL template data (blockType, name, description, etc.)
          id: uuid,         // Firestore ID
          entityId: uuid,   // Entity ID (PRIMARY KEY)
          position,         // Override position with drop position
        } as StrategyBlock;

        // Use createBlock() to handle registry, canvas, and Firebase
        const result = await this.createBlock(newBlock, position);
        return {
          entity: result.firebaseBlock,
        };
      }

      console.warn('[BlockFlowCoordinator] Template not found:', templateId);
    }

    // Fallback: create block without template (backward compatibility)
    const blockType = data.blockType || 'custom';
    const name = data.name || `Block ${Date.now()}`;

    const result = await this.createBlock({ ...data, blockType, name }, position);

    return {
      entity: result.firebaseBlock,
    };
  }

  /**
   * Update entity (FlowCoordinator interface)
   */
  updateEntity(blockId: string, updates: Partial<StrategyBlock>): void {
    this.updateBlock(blockId, updates);
  }

  /**
   * Delete entities (FlowCoordinator interface)
   * Returns Promise with deleted transition IDs
   */
  async deleteEntities(blockIds: string[]): Promise<{
    deletedTransitionIds: string[];
    affectedScreensBackup?: Map<string, any>;
  }> {
    const deletedTransitionIds = await this.deleteBlocks(blockIds);
    return {
      deletedTransitionIds,
      affectedScreensBackup: undefined
    };
  }

  /**
   * Remove one screenless library draft and only the transitions connected to
   * that draft. This deliberately does not broaden the generic delete path:
   * the persisted adaptation marker is the authorization boundary.
   */
  async deleteLibraryDraft(entityId: string): Promise<string[]> {
    const block = blockRegistry.get(entityId)
      ?? blockRegistry.getAll().find(candidate => candidate.id === entityId);
    const adaptation = block ? readLibraryAdaptationState(block) : undefined;
    if (!block?.id || !block.entityId || !adaptation
      || adaptation.mode !== 'staged' || adaptation.status === 'adapted') {
      throw new Error('Only an unadapted staged library draft can be removed through this action.');
    }

    const affectedTransitions = this.computeAllTransitions().filter(transition => (
      transition.sourceEntityId === block.entityId
      || transition.sourceEntityId === block.id
      || transition.targetEntityId === block.entityId
      || transition.targetEntityId === block.id
    ));
    const transitionIds = affectedTransitions.map(transition => transition.id);
    if (transitionIds.length > 0) {
      // Must run while all source blocks remain registered so incoming edges
      // are removed from their persisted `action.edges` arrays.
      await this.deleteTransitions(transitionIds);
    }
    await this.wrapWithLoading('Removing library draft', async () => {
      await this.blockService!.delete(block.id!);
    });
    blockRegistry.delete(block.entityId);
    this.canvasHandle?.removeNodes([block.entityId]);
    return transitionIds;
  }

  /**
   * Restore entities (FlowCoordinator interface)
   * Restores deleted blocks to Registry, Canvas, and Firebase
   */
  async restoreEntities(blocks: StrategyBlock[]): Promise<void> {
    console.log(`[BlockCoordinator] Restoring ${blocks.length} block(s)`);

    const allTransitions: EntityTransition[] = [];

    // Restore to registry and canvas
    for (const block of blocks) {
      if (!block.id || !block.entityId) {
        console.warn('[BlockCoordinator] Block missing id or entityId, skipping:', block);
        continue;
      }

      // Register in BlockRegistry
      blockRegistry.register(block);

      // Add to Canvas
      const canvasBlock = blockConverter.toCanvasBlock(block);
      this.canvasHandle?.addNode({
        entityId: block.entityId,
        entityType: 'block',
        nodeType: 'strategy-block',
        position: canvasBlock.position,
        handles: canvasBlock.connectionHandles || [],
        isLaunchScreen: false,
      });

      // Extract transitions from restored block
      const blockTransitions = blockTransitionManager.extractTransitions(block);
      allTransitions.push(...blockTransitions);
    }

    // Restore to Firebase with loading indicator - use update() to preserve original IDs
    await this.wrapWithLoading(`Restoring ${blocks.length} block(s)`, async () => {
      await Promise.all(blocks.map(block => this.blockService!.update(block.id!, block)));
    });

    // Add edges to canvas for restored blocks
    allTransitions.forEach(transition => {
      this.executeCanvasOperation(handle =>
        handle.addEdge(this.createEdgeData(transition))
      );
    });

    console.log(`[BlockCoordinator] ✅ Restored ${blocks.length} block(s) with ${allTransitions.length} edge(s)`);
  }

  // ============================================================================
  // SECTION 5: BLOCK-SPECIFIC OPERATIONS
  // ============================================================================

  /**
   * Create a new block
   */
  async createBlock(data: Partial<StrategyBlock>, position: { x: number; y: number }): Promise<CreateBlockResult> {
    // Generate UUID for both id and entityId (consistent with dual ID architecture)
    const blockUuid = crypto.randomUUID();

    const newBlock: StrategyBlock = {
      ...data,
      id: blockUuid,       // Use UUID for Firestore document ID
      entityId: blockUuid, // Use same UUID for semantic entity ID
      position: { x: position.x, y: position.y },
      order: data.order ?? 0,
      createdAt: new Date(),
    } as StrategyBlock;

    console.log(`[BlockCoordinator] Creating block in Firebase: ${blockUuid}`);

    // Register in BlockRegistry
    blockRegistry.register(newBlock);

    // Add to Canvas imperatively (unified NodeData interface)
    const canvasBlock = blockConverter.toCanvasBlock(newBlock);
    this.canvasHandle?.addNode({
      entityId: newBlock.entityId!,  // Use entityId for canvas
      entityType: 'block',
      nodeType: 'strategy-block',
      position: canvasBlock.position,
      handles: canvasBlock.connectionHandles || [],
      isLaunchScreen: false,
    });

    // CRITICAL: AWAIT Firebase save before returning (required for cloning)
    // Firebase returns block with correct Firestore document ID
    let firebaseBlock = newBlock;

    await this.wrapWithLoading('Creating block', async () => {
      firebaseBlock = await this.blockService!.create(newBlock);
      // Update Registry with Firebase-returned block (has correct Firestore ID)
      blockRegistry.register(firebaseBlock);
    });

    // Note: No need to sync edges - new block has no transitions yet

    return { firebaseBlock: firebaseBlock };
  }

  /**
   * Update a block (supports both entityId and Firestore id)
   */
  async updateBlock(blockId: string, updates: Partial<StrategyBlock>): Promise<void> {
    const block = blockRegistry.get(blockId);
    if (!block) {
      console.warn(`[BlockCoordinator] Block ${blockId} not found`);
      return;
    }

    const firestoreId = block.id!;
    const entityId = block.entityId!;

    const updatedBlock = { ...block, ...updates };
    blockRegistry.update(entityId, updates);

    // Update canvas (use entityId)
    this.canvasHandle?.updateNodeData(entityId, updates as any);

    // Save to Firebase (use Firestore id)
    if (this.blockService) {
      await this.wrapWithLoading('Updating block', async () => {
        await this.blockService!.update(firestoreId, updatedBlock);
      });
    }
  }

  /**
   * Update entity and refresh all connections/transitions (FlowCoordinator interface)
   * Handles: Registry, Canvas, Firebase, Transitions
   * Used by: EditEntityCommand, BlockFormDialog
   */
  async updateEntityAndRefreshConnections(blockId: string, entity: StrategyBlock): Promise<void> {
    const block = blockRegistry.get(blockId);
    if (!block) {
      throw new Error(`Block not found: ${blockId}`);
    }

    // Merge updates with existing entity (preserves all fields)
    const updatedBlock = { ...block, ...entity };

    // STEP 1: Update Registry
    blockRegistry.update(blockId, updatedBlock);

    // STEP 2: Update Canvas (blocks have simpler handles than screens)
    this.executeCanvasOperation(handle =>
      handle.updateNodeData(blockId, updatedBlock as any)
    );

    // STEP 3: Refresh all transitions
    const allEdges = this.computeAllTransitions();
    this.executeCanvasOperation(handle => handle.syncEdges(allEdges));

    // STEP 4: Update Firebase (use Firestore ID)
    const firestoreId = block.id!;
    if (this.blockService) {
      await this.wrapWithLoading('Updating block', async () => {
        await this.blockService!.update(firestoreId, updatedBlock);
        console.log(`[BlockCoordinator] Updated block ${blockId.slice(0, 8)} in Firebase`);
      });
    }
  }

  /**
   * Delete blocks
   * Returns array of deleted transition IDs
   */
  async deleteBlocks(blockIds: string[]): Promise<string[]> {
    const deletedTransitionIds: string[] = [];
    const firestoreIds: string[] = [];

    for (const blockId of blockIds) {
      const block = blockRegistry.get(blockId);
      if (!block) {
        console.warn(`[BlockCoordinator] deleteBlocks: Block not found: ${blockId}`);
        continue;
      }

      const blockEntityId = block.entityId!;
      const firestoreId = block.id!;

      console.log(`[BlockCoordinator] deleteBlocks: blockId=${blockId}, entityId=${blockEntityId}, firestoreId=${firestoreId}`);

      // Get all transitions that will be deleted
      const transitions = this.computeAllTransitions();

      // Match transitions where:
      // - Source is this block (by Firestore id OR entityId)
      // - Target is this block (by Firestore id OR entityId)
      const affectedTransitions = transitions.filter(t => {
        const matchesSource = t.sourceEntityId === firestoreId ||
                             (blockEntityId && t.sourceEntityId === blockEntityId);
        const matchesTarget = t.targetEntityId === firestoreId ||
                              (blockEntityId && t.targetEntityId === blockEntityId);
        return matchesSource || matchesTarget;
      });

      console.log(`[BlockCoordinator] deleteBlocks: Found ${affectedTransitions.length} affected transitions`);
      deletedTransitionIds.push(...affectedTransitions.map(t => t.id));

      // Remove from registry (use entityId)
      blockRegistry.delete(blockEntityId);

      // Remove from canvas (use entityId)
      this.canvasHandle?.removeNodes([blockEntityId]);

      // Collect Firestore IDs for batch delete
      firestoreIds.push(firestoreId);
    }

    // Delete from Firebase with loading indicator (batch operation)
    await this.wrapWithLoading(`Deleting ${blockIds.length} block(s)`, async () => {
      await Promise.all(firestoreIds.map(id => this.blockService!.delete(id)));
    });

    // NOTE: No need to manually remove edges from canvas - ReactFlow automatically
    // removes connected edges when a node is deleted (line 373).
    // Attempting to remove them again would trigger deleteTransitions() which fails
    // because the block is already deleted from registry.

    return deletedTransitionIds;
  }

  /**
   * Get all blocks
   */
  getAllBlocks(): StrategyBlock[] {
    return blockRegistry.getAll();
  }

  /**
   * Get block by entityId
   * @param id - Block entityId
   * @returns Block or undefined if not found
   */
  public getBlock(id: string): StrategyBlock | undefined {
    return blockRegistry.get(id);
  }

  /**
   * Generic entity getter (for compatibility with generic event handlers)
   * @param id - Entity ID (entityId or Firestore id)
   * @returns Block or undefined if not found
   */
  public getEntity(id: string): StrategyBlock | undefined {
    return this.getBlock(id);
  }

  /**
   * Get block position by ID
   * @param id - Block ID
   * @returns Block position
   * @throws Error if block not found
   */
  public getEntityPosition(id: string): XyPoint {
    const block = this.getBlock(id);
    if (!block) {
      throw new Error(`Block not found: ${id}`);
    }
    return block.position ?? { x: 0, y: 0 };
  }

  /**
   * Restore deleted blocks (for undo operation)
   * @param blocks - Array of blocks to restore
   */
  public async restoreBlocks(blocks: StrategyBlock[]): Promise<void> {
    const allTransitions: EntityTransition[] = [];

    for (const block of blocks) {
      // Register in memory
      blockRegistry.register(block);

      // Add to canvas with handles (unified NodeData interface)
      if (this.canvasHandle) {
        const canvasBlock = blockConverter.toCanvasBlock(block);
        this.canvasHandle.addNode({
          entityId: block.entityId!,
          entityType: 'block',
          nodeType: 'strategy-block',
          position: canvasBlock.position,
          handles: canvasBlock.connectionHandles || [],
          isLaunchScreen: false
        });
      }

      // Collect transitions from restored blocks
      const blockTransitions = blockTransitionManager.extractTransitions(block);
      allTransitions.push(...blockTransitions);
    }

    // Restore to Firebase
    if (this.blockService) {
      await this.wrapWithLoading(`Restoring ${blocks.length} block(s)`, async () => {
        await Promise.all(blocks.map(block => this.blockService!.create(block)));
      });
    }

    // Add edges for restored blocks (point updates)
    allTransitions.forEach(transition => {
      this.executeCanvasOperation(handle =>
        handle.addEdge(this.createEdgeData(transition))
      );
    });
  }

  /**
   * Update entity positions (FlowCoordinator interface - unified method)
   * Handles: Registry, Canvas, Firebase with proper async/await
   * Used by: MoveEntityCommand, generic drag-and-drop operations
   */
  async updateEntityPositions(moves: Array<{
    entityId: string;
    oldPosition: XyPoint;
    newPosition: XyPoint;
  }>): Promise<void> {
    // STEP 1: Update Registry
    moves.forEach(move => {
      const position = {
        x: move.newPosition.x ?? 0,
        y: move.newPosition.y ?? 0
      };
      blockRegistry.updatePosition(move.entityId, position);
    });

    // STEP 2: Update Firebase with proper await (use Firestore id)
    if (this.blockService) {
      await this.wrapWithLoading(`Updating ${moves.length} block position(s)`, async () => {
        await Promise.all(moves.map(({ entityId, newPosition }) => {
          const block = blockRegistry.get(entityId);
          if (!block?.id) {
            console.warn(`[BlockCoordinator] Cannot update Firebase position - block ${entityId} not found or missing id`);
            return Promise.resolve();
          }
          return this.blockService!.update(block.id, { position: newPosition } as any);
        }));
        console.log(`[BlockCoordinator] Updated ${moves.length} positions in Firebase`);
      });
    }

    // STEP 3: Update Canvas
    this.executeCanvasOperation(handle => handle.updateNodePositions(moves.map(m => ({
      entityId: m.entityId,
      oldPosition: {
        x: m.oldPosition.x,
        y: m.oldPosition.y
      },
      newPosition: {
        x: m.newPosition.x,
        y: m.newPosition.y
      }
    }))));
  }

  /**
   * Clone entity with new UUID and position offset (FlowCoordinator interface)
   *
   * ⚠️ IMPORTANT: All edges are CLEARED - no transitions are cloned
   *
   * Creates a deep copy of the source block with:
   * - New UUID for id and entityId
   * - All edges removed (clean copy via clearAllBlockEdges)
   * - Position offset from original
   * - Name appended with " (Copy)"
   * - All child screens cloned (without transitions)
   *
   * Handles: Deep copy, edge clearing, screen cloning, Registry registration, Canvas addition, Firebase persistence
   *
   * @param sourceEntityId - Block ID to clone
   * @param offset - Position offset { x, y } from original block
   * @returns Promise resolving with cloned block with new ID and empty edges
   */
  async cloneEntity(sourceEntityId: string, offset: XyPoint): Promise<CreateEntityResult<StrategyBlock>> {
    console.log(`[BlockCoordinator] Cloning block: ${sourceEntityId}`);

    // STEP 1: Get source block from Registry
    const sourceBlock = this.getBlock(sourceEntityId);
    if (!sourceBlock) {
      throw new Error(`Source block ${sourceEntityId} not found`);
    }

    console.log(`[BlockCoordinator] 🔍 SOURCE BLOCK DEBUG:`);
    console.log(`  sourceEntityId (input): ${sourceEntityId}`);
    console.log(`  sourceBlock.entityId: ${sourceBlock.entityId}`);
    console.log(`  sourceBlock.id (Firestore): ${sourceBlock.id}`);
    console.log(`  sourceBlock.name: ${sourceBlock.name}`);

    // STEP 2: Get Firestore document ID for cloning
    const sourceBlockFirestoreId = sourceBlock.id;
    if (!sourceBlockFirestoreId) {
      throw new Error(`Source block ${sourceEntityId} missing Firestore ID`);
    }
    console.log(`[BlockCoordinator] Source Firestore ID for cloning: ${sourceBlockFirestoreId}`);

    // STEP 3: Calculate new position
    const newPosition = {
      x: (sourceBlock.position?.x ?? 0) + (offset.x ?? 0),
      y: (sourceBlock.position?.y ?? 0) + (offset.y ?? 0)
    };

    // STEP 4: Use StrategyService to clone block with all screens
    // This handles: entityId generation, edge clearing, screen cloning, Firebase persistence
    const strategyService = new StrategyService(this.projectId);
    const clonedBlock = await this.wrapWithLoading('Cloning block', async () => {
      return await strategyService.cloneBlock(this.strategyId, sourceBlockFirestoreId);
    });

    console.log(`[BlockCoordinator] ✅ Block cloned via StrategyService: ${clonedBlock.id} (entityId: ${clonedBlock.entityId})`);

    // STEP 5: Update position (StrategyService doesn't handle position offset)
    clonedBlock.position = newPosition;
    await this.wrapWithLoading('Updating cloned block position', async () => {
      await this.blockService!.update(clonedBlock.id!, { position: newPosition });
    });
    console.log(`[BlockCoordinator] Updated cloned block position:`, newPosition);

    // STEP 6: Register in Registry and add to Canvas
    blockRegistry.register(clonedBlock);
    if (this.canvasHandle) {
      const canvasBlock = blockConverter.toCanvasBlock(clonedBlock);
      this.canvasHandle.addNode({
        entityId: clonedBlock.entityId!,  // Use entityId for canvas
        entityType: 'block',
        nodeType: 'strategy-block',
        position: canvasBlock.position,
        handles: canvasBlock.connectionHandles || [],
        isLaunchScreen: false,
      });
      console.log(`[BlockCoordinator] Added cloned block to Canvas: ${clonedBlock.entityId}`);
    }

    return { entity: clonedBlock };
  }

  /**
   * Update positions for multiple blocks (batch operation)
   * @param moves - Array of block moves
   */
  public async updateBlockPositions(moves: Array<{ blockId: string; newPosition: XyPoint }>): Promise<void> {
    // Update positions in registry
    for (const move of moves) {
      const block = this.getBlock(move.blockId);
      if (!block) {
        console.warn(`[BlockCoordinator] Block not found for position update: ${move.blockId}`);
        continue;
      }

      // Ensure position has valid coordinates
      const position = {
        x: move.newPosition.x ?? 0,
        y: move.newPosition.y ?? 0
      };

      // Update position in registry (silent - no notifications)
      blockRegistry.updatePosition(move.blockId, position);
    }

    // Update Firebase (batch)
    if (this.blockService) {
      await this.wrapWithLoading(`Updating ${moves.length} block position(s)`, async () => {
        await Promise.all(moves.map(move => {
          const position = {
            x: move.newPosition.x ?? 0,
            y: move.newPosition.y ?? 0
          };
          const firestoreId = this.resolveToFirestoreId(move.blockId);
          if (!firestoreId) {
            throw new Error(`Block not found for position persistence: ${move.blockId}`);
          }
          return this.blockService!.update(firestoreId, { position } as any);
        }));
      });
    }

    // Update canvas positions (batch - no re-renders)
    if (this.canvasHandle) {
      this.canvasHandle.updateNodePositions(moves.map(m => {
        const oldBlock = this.getBlock(m.blockId);
        const oldPos = oldBlock?.position ?? { x: 0, y: 0 };
        return {
          entityId: m.blockId,
          oldPosition: {
            x: oldPos.x ?? 0,
            y: oldPos.y ?? 0
          },
          newPosition: {
            x: m.newPosition.x ?? 0,
            y: m.newPosition.y ?? 0
          }
        };
      }));
    }
  }

  // ============================================================================
  // SECTION 6: TRANSITION OPERATIONS (FlowCoordinator interface)
  // ============================================================================

  /**
   * Create transition (FlowCoordinator interface)
   */
  async createTransition(input: CreateTransitionInput): Promise<EntityTransition> {
    console.log('[BlockFlowCoordinator] createTransition called with:', {
      sourceEntityId: input.sourceEntityId,
      targetEntityId: input.targetEntityId,
      sourceActionKey: input.sourceActionKey,
      label: input.label
    });

    if (!input.sourceEntityId || !input.targetEntityId) {
      throw new Error(`Invalid input: sourceEntityId="${input.sourceEntityId}", targetEntityId="${input.targetEntityId}"`);
    }

    const sourceBlock = blockRegistry.get(input.sourceEntityId);
    if (!sourceBlock) {
      throw new Error(`Source block ${input.sourceEntityId} not found`);
    }

    const targetBlock = blockRegistry.get(input.targetEntityId);
    if (!targetBlock) {
      throw new Error(`Target block ${input.targetEntityId} not found`);
    }

    // Generate transition ID
    const transitionId = TransitionIdGenerator.generate(
      input.sourceEntityId,
      input.sourceActionKey || 'action',
      input.targetEntityId
    );

    // Add transition using BlockTransitionManager
    const transition: EntityTransition = {
      id: transitionId,
      sourceEntityId: input.sourceEntityId,
      targetEntityId: input.targetEntityId,
      sourceActionKey: input.sourceActionKey || 'action',
      label: input.label,
    };

    const updatedBlock = blockTransitionManager.addTransition(sourceBlock, transition);

    // Update registry
    blockRegistry.update(input.sourceEntityId, updatedBlock);

    // Add single edge to canvas (point update)
    this.executeCanvasOperation(handle =>
      handle.addEdge(this.createEdgeData(transition))
    );

    // Save to Firebase (resolve entityId to Firestore id)
    const sourceFirestoreId = this.resolveToFirestoreId(input.sourceEntityId);
    await this.wrapWithLoading('Creating transition', async () => {
      await this.blockService!.update(sourceFirestoreId, updatedBlock);
    });

    return transition;
  }

  /**
   * Update transition (FlowCoordinator interface)
   */
  async updateTransition(transitionId: string, updates: Partial<Omit<EntityTransition, 'id'>>): Promise<EntityTransition> {
    const parsed = TransitionIdGenerator.parse(transitionId);
    if (!parsed) {
      throw new Error(`Invalid transition ID: ${transitionId}`);
    }

    const sourceBlock = blockRegistry.get(parsed.sourceEntityId);
    if (!sourceBlock) {
      throw new Error(`Source block ${parsed.sourceEntityId} not found`);
    }

    // Update transition using BlockTransitionManager
    const updatedBlock = blockTransitionManager.updateTransition(
      sourceBlock,
      transitionId,
      updates
    );

    // Update registry
    blockRegistry.update(parsed.sourceEntityId, updatedBlock);

    // Return updated transition
    const transitions = blockTransitionManager.extractTransitions(updatedBlock);
    const updatedTransition = transitions.find(t => t.id === transitionId);
    if (!updatedTransition) {
      throw new Error(`Updated transition not found: ${transitionId}`);
    }

    // Update single edge on canvas (point update)
    this.executeCanvasOperation(handle =>
      handle.updateEdge(this.createEdgeData(updatedTransition))
    );

    // Save to Firebase (resolve entityId to Firestore id)
    const sourceFirestoreId = this.resolveToFirestoreId(parsed.sourceEntityId);
    await this.wrapWithLoading('Updating transition', async () => {
      await this.blockService!.update(sourceFirestoreId, updatedBlock);
    });

    return updatedTransition;
  }

  /**
   * Delete transitions (FlowCoordinator interface)
   */
  async deleteTransitions(transitionIds: string[]): Promise<void> {
    console.log('[BlockCoordinator] deleteTransitions called with:', transitionIds);

    const blockUpdates = new Map<string, StrategyBlock>();

    for (const transitionId of transitionIds) {
      const parsed = TransitionIdGenerator.parse(transitionId);
      if (!parsed) {
        console.warn('[BlockCoordinator] Failed to parse transition ID:', transitionId);
        continue;
      }

      console.log('[BlockCoordinator] Processing transition:', {
        id: transitionId,
        sourceEntityId: parsed.sourceEntityId,
        targetEntityId: parsed.targetEntityId,
      });

      const sourceBlock = blockRegistry.get(parsed.sourceEntityId);
      if (!sourceBlock) {
        console.warn('[BlockCoordinator] Source block not found:', parsed.sourceEntityId);
        continue;
      }

      console.log('[BlockCoordinator] Before deletion, block edges:', sourceBlock.action?.edges);

      // Remove transition using BlockTransitionManager
      const currentBlock = blockUpdates.get(parsed.sourceEntityId) || sourceBlock;
      const updatedBlock = blockTransitionManager.removeTransition(currentBlock, transitionId);
      blockUpdates.set(parsed.sourceEntityId, updatedBlock);

      console.log('[BlockCoordinator] After deletion, block edges:', updatedBlock.action?.edges);
    }

    // Update registry
    blockUpdates.forEach((block, entityId) => {
      blockRegistry.update(entityId, block);
    });

    // Remove edges from canvas (point update)
    this.executeCanvasOperation(handle => handle.removeEdges(transitionIds));

    // Save to Firebase (batch update)
    await this.wrapWithLoading(`Deleting ${transitionIds.length} transition(s)`, async () => {
      await Promise.all(
        Array.from(blockUpdates.entries()).map(([entityId, block]) => {
          const firestoreId = this.resolveToFirestoreId(entityId);
          return this.blockService!.update(firestoreId, block);
        })
      );
    });
  }

  // ============================================================================
  // SECTION 7: CANVAS OPERATIONS
  // ============================================================================

  /**
   * Load existing blocks to canvas
   */
  private loadExistingBlocksToCanvas(): void {
    if (!this.canvasHandle) {
      console.warn('[BlockCoordinator] Cannot load blocks - no canvas handle');
      return;
    }

    const blocks = blockRegistry.getAll();

    // DEBUG: Детальна інформація про блоки та edges
    console.log('🔍 [BlockCoordinator] === ДЕТАЛЬНА ІНФОРМАЦІЯ ПРО БЛОКИ ТА EDGES ===');
    blocks.forEach((block, index) => {
      console.log(`\nБлок #${index + 1}: ${block.id}`);
      console.log(`  entityId: ${block.entityId || 'undefined'}`);
      console.log(`  name: ${block.name}`);
      console.log(`  action:`, block.action);
      console.log(`  action.edges:`, block.action?.edges || []);
    });
    console.log('🔍 [BlockCoordinator] ===================================================\n');

    for (const block of blocks) {
      const canvasBlock = blockConverter.toCanvasBlock(block);
      this.canvasHandle.addNode({
        entityId: block.entityId!,
        entityType: 'block',
        nodeType: 'strategy-block',
        position: canvasBlock.position,
        handles: canvasBlock.connectionHandles || [],
        isLaunchScreen: false,
      });
    }

    this.syncAllEdges();
  }

  /**
   * Sync all edges on canvas
   * FR-P3-005: Computed on-demand from block.action.edges[]
   */
  private syncAllEdges(): void {
    if (!this.canvasHandle) return;

    const transitions = this.computeAllTransitions();

    // DEBUG: Детальний лог transitions для відладки
    console.log('🔗 [BlockCoordinator] syncAllEdges() - transitions:', {
      count: transitions.length,
      transitions: transitions.map(t => ({
        id: t.id,
        source: t.sourceEntityId,
        target: t.targetEntityId,
        actionKey: t.sourceActionKey,
      })),
    });

    this.canvasHandle.syncEdges(transitions as any);
  }

  /**
   * Compute all transitions from blocks
   * FR-P3-005: On-demand computation (NO cache)
   */
  private computeAllTransitions(): EntityTransition[] {
    const allBlocks = blockRegistry.getAll();
    const transitions: EntityTransition[] = [];

    for (const block of allBlocks) {
      const blockTransitions = blockTransitionManager.extractTransitions(block);
      transitions.push(...blockTransitions);
    }

    // Validate transitions - filter out those with non-existent targets
    const validatedTransitions = transitions.filter(t => {
      const targetBlock = blockRegistry.get(t.targetEntityId);

      if (!targetBlock) {
        console.error(`[BlockCoordinator] ❌ CRITICAL: Transition target not found: ${t.targetEntityId} (transition: ${t.id})`);
        console.error(`  Available blocks:`, blockRegistry.getAll().map(b => ({ id: b.id, entityId: b.entityId })));
        return false;
      }
      return true;
    });

    return validatedTransitions;
  }

  // ============================================================================
  // SECTION 8: CANVAS OPERATION HELPERS
  // ============================================================================

  /**
   * Execute canvas operation with null-safety check
   * @param operation - Canvas operation to execute
   */
  private executeCanvasOperation(operation: (handle: ReactFlowCanvasHandle) => void): void {
    if (!this.canvasHandle) {
      console.warn('[BlockCoordinator] Canvas operation skipped - no canvas handle');
      return;
    }
    operation(this.canvasHandle);
  }

  /**
   * Create EdgeData for ReactFlow from EntityTransition
   *
   * CRITICAL: Transitions use entityId (UUID) but Canvas nodes use Firestore id.
   * This method resolves entityId → Firestore id for Canvas compatibility.
   *
   * @param transition - Transition to convert
   * @returns EdgeData object for ReactFlow
   */
  private createEdgeData(transition: EntityTransition): any {
    // CRITICAL: Both transitions and Canvas nodes use entityId (semantic UUID).
    // Firestore id is used only for Firebase operations via entity.id property.
    return {
      id: transition.id,
      source: transition.sourceEntityId,
      target: transition.targetEntityId,
      sourceHandle: TransitionIdGenerator.generateHandleId(
        transition.sourceEntityId,
        transition.sourceActionKey
      ),
      label: transition.label ?? '',
      type: 'transition',
      animated: false,
      data: {
        sourceEntityId: transition.sourceEntityId,
        targetEntityId: transition.targetEntityId,
        sourceActionKey: transition.sourceActionKey
      }
    };
  }

 

  /**
   * Resolve entityId to Firestore document ID
   *
   * @param entityId - Entity ID
   * @returns Firestore document ID
   */
  private resolveToFirestoreId(entityId: string): string {
    const block = blockRegistry.get(entityId);
    if (!block) {
      console.error(`[BlockCoordinator] ❌ Cannot resolve entityId to Firestore ID: ${entityId}`);
      throw new Error(`Block not found: ${entityId}`);
    }
    return block.id!;
  }

  // ============================================================================
  // SECTION 10: DEBUG
  // ============================================================================

  debug(): void {
    console.log('[BlockCoordinator] Debug:');
    const transitions = this.computeAllTransitions();
    console.log(`- Transitions: ${transitions.length}`);
    transitions.forEach((t) => {
      console.log(`  ${t.id}: ${t.sourceEntityId} → ${t.targetEntityId} (${t.sourceActionKey})`);
    });
    blockRegistry.debug();
  }
}
